import React, { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  Stack,
} from "@mui/material";
import { DirectionsBus as BusIcon } from "@mui/icons-material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useToast } from "../../../components/Toast";
import { Loading } from "../../../components/Loading";
import SearchableAutocomplete from "../../../components/SearchableAutocomplete";
import { bookingApi } from "../apiService";

interface AddScheduleVehicleModalProps {
  open: boolean;
  onClose: () => void;
  rentalJourneyId: string;
  onSuccess: (
    vehicleId: string,
    vehicleNumber: string,
    vehicleType: string
  ) => void;
}

interface VehicleFormData {
  vehicleId: string | null;
  vehicleNumber: string;
  vehicleType: string;
}

const validationSchema = Yup.object().shape({
  vehicleId: Yup.string().required("Vehicle selection is required"),
  vehicleNumber: Yup.string().required("Vehicle number is required"),
  vehicleType: Yup.string().required("Vehicle type is required"),
});

const AddScheduleVehicleModal: React.FC<AddScheduleVehicleModalProps> = ({
  open,
  onClose,
  rentalJourneyId,
  onSuccess,
}) => {
  const [selectedVehicle, setSelectedVehicle] = useState<any | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  const initialValues: VehicleFormData = {
    vehicleId: null,
    vehicleNumber: "",
    vehicleType: "",
  };

  const handleVehicleChange = (newValue: any, setFieldValue: any) => {
    if (newValue) {
      const vehicleType =
        newValue?.acquisitionType === "Outsourced" ? "OUTSOURCED" : "OWNED";

      setSelectedVehicle(newValue);
      setFieldValue("vehicleId", newValue?.id || null);
      setFieldValue("vehicleNumber", newValue?.vehicleNumber || "");
      setFieldValue("vehicleType", vehicleType || "");
    } else {
      setSelectedVehicle(null);
      setFieldValue("vehicleId", null);
      setFieldValue("vehicleNumber", "");
      setFieldValue("vehicleType", "");
    }
  };

  const handleSubmit = async (values: VehicleFormData) => {
    setIsSubmitting(true);
    try {
      const response = await bookingApi.updateBooking(rentalJourneyId, {
        vehicleId: values.vehicleId,
        vehicleNumber: values.vehicleNumber,
        vehicleType: values.vehicleType,
      });
      if (response) {
        showToast("Vehicle added successfully", "success");
        onSuccess(
          values?.vehicleId || "",
          values?.vehicleNumber,
          values?.vehicleType
        );
        onClose();
      } else {
        showToast();
      }
    } catch (error) {
      console.error("Error selecting vehicle:", error);
      showToast();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <BusIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Select Vehicle
          </Typography>
        </Stack>
      </DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, setFieldValue, isValid }) => (
          <Form>
            <DialogContent sx={{ pb: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <SearchableAutocomplete
                    entityType="VEHICLE"
                    value={selectedVehicle}
                    onSelect={(vehicle) => {
                      handleVehicleChange(vehicle, setFieldValue);
                    }}
                    onChange={(value) => {
                      // Handle text input changes if needed
                    }}
                    label="Select Vehicle"
                    placeholder="Choose a vehicle"
                    getOptionLabel={(option) =>
                      typeof option === "string"
                        ? option
                        : `${option?.vehicleNumber}, ${
                            option?.acquisitionType
                          }, (${option?.seatingCapacity || 0} seats)`
                    }
                    renderOptionContent={(vehicle) => (
                      <Stack spacing={0.5} sx={{ width: "100%" }}>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 500, fontSize: "0.75rem" }}
                        >
                          {vehicle?.vehicleNumber}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: "0.65rem" }}
                        >
                          {vehicle?.acquisitionType}, (
                          {vehicle?.seatingCapacity || 0} seats)
                        </Typography>
                      </Stack>
                    )}
                    noOptionsText="No vehicles found"
                    size="small"
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{ height: "30px", fontSize: "0.75rem" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={!values.vehicleId || isSubmitting}
                sx={{ height: "30px", fontSize: "0.75rem" }}
              >
                {isSubmitting ? "Adding..." : "Add Vehicle"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddScheduleVehicleModal;
